import dayjs from "dayjs";

/*
 * The batch registry, as the vaccination form needs to see it (KIN-1740).
 *
 * `GET {tenant}/vaccine-batches` answers with HSE's national batch list plus this
 * practice's own private batches. Every batch carries its number and expiry; most, but
 * not all, also carry the catalogue product they are of. Phase 2 §4.4.4 makes the batch
 * the entry point — picking one "should trigger the Product, dose, and Trade name to be
 * displayed on the form" — so these helpers turn a batch into what the picker shows and
 * what the form stores, and keep the awkward rows honest rather than hiding them.
 */

// What a batch cannot tell us about the product it is of.
const NO_PRODUCT_LABEL = "no product matched";

const formatExpiry = (expiryDate) =>
  expiryDate ? dayjs(expiryDate).format("DD MMM YYYY") : "";

/**
 * How the batch names its vaccine, for the option line.
 *
 * HSE's report names a product only by a pack description, which is matched to our
 * catalogue by a mapping table. A row not mapped yet — flu and COVID-19 batches, until
 * their products are confirmed — has no `productName`, so the pack description stands in
 * for it. It describes the vial honestly without claiming a product the batch cannot
 * fill in.
 */
export const getBatchProductLabel = (batch) =>
  batch?.productName || batch?.supplierDescription || NO_PRODUCT_LABEL;

/**
 * The one line a batch gets in the picker: what it is, when it expires, and anything
 * about it a clinician must know before choosing it.
 *
 * A private batch is a practice's own purchase and is not on HSE's list, so a dose from
 * one must not be claimed from PCRS. That is said on the option itself rather than only
 * after it is chosen, because by then the choice has been made.
 */
export const getBatchOptionLabel = (batch) => {
  if (!batch) return "";

  const expiry = formatExpiry(batch.expiryDate);
  const parts = [getBatchProductLabel(batch)];

  if (expiry) {
    parts.push(`expires ${expiry}`);
  }

  if (batch.isPublic === false) {
    parts.push("private stock, not claimable from PCRS");
  }

  // HSE no longer list it, but stock already delivered is still stock, so it is offered
  // rather than withheld — said plainly so nobody reaches for a vial they no longer have.
  if (batch.droppedFromReport) {
    parts.push("no longer on HSE's list");
  }

  // The batch recorded against an existing dose, kept in the list so reopening that
  // record still shows what it was given from even once HSE have dropped or expired it.
  if (batch.isRecorded) {
    parts.push("as recorded");
  }

  return `${batch.batchNumber} — ${parts.join(" · ")}`;
};

/**
 * Whether a batch can be recorded against a dose of this product.
 *
 * The backend refuses (400) a batch whose product differs from the dose's, so a list
 * that offered one would be offering a refusal. A batch with no product matched fits any
 * dose: it supplies only the number and expiry.
 */
export const isBatchForProduct = (batch, vaccineCodeId) => {
  if (!vaccineCodeId) return true;
  if (batch?.vaccineCodeId === null || batch?.vaccineCodeId === undefined) return true;

  return String(batch.vaccineCodeId) === String(vaccineCodeId);
};

/**
 * The batch the record already names, as an option the picker can display.
 *
 * The list is fetched for the batches in date on the vaccination date, so the batch an
 * older dose was given from may no longer be in it. Without this the field would reopen
 * blank on a record that plainly has a batch. Built from the dose's own stored copy —
 * the backend keeps the number and expiry it was linked with — not from the live batch.
 */
export const toRecordedBatchOption = ({ vaccineBatchId, batchNo, expiryDate }) => {
  if (!vaccineBatchId) return null;

  return {
    id: vaccineBatchId,
    batchNumber: batchNo || "",
    expiryDate: expiryDate || null,
    vaccineCodeId: null,
    productName: null,
    supplierDescription: null,
    isPublic: true,
    droppedFromReport: false,
    isRecorded: true,
  };
};

/**
 * The options the batch picker offers: the batches that fit the chosen product, plus the
 * one the record already names when the list no longer holds it.
 */
export const buildBatchOptions = ({
  batches = [],
  vaccineCodeId = null,
  recordedBatch = null,
}) => {
  const fitting = batches.filter((batch) => isBatchForProduct(batch, vaccineCodeId));
  const isRecordedListed =
    !!recordedBatch &&
    fitting.some((batch) => String(batch.id) === String(recordedBatch.id));
  const withRecorded =
    recordedBatch && !isRecordedListed ? [recordedBatch, ...fitting] : fitting;

  return withRecorded.map((batch) => ({
    value: String(batch.id),
    label: getBatchOptionLabel(batch),
    batch,
  }));
};

export const findBatchById = (batches = [], batchId) =>
  batches.find((batch) => String(batch.id) === String(batchId)) || null;

/**
 * What choosing a batch writes into the form.
 *
 * The batch number and expiry stop being typed — the two the ticket names as mistyped
 * and as disagreeing with each other. `vaccineCodeId` is the product the batch is of, or
 * null when it is matched to none, in which case the product stays whatever the
 * clinician chose.
 */
export const getBatchFormValues = (batch) => {
  if (!batch) return null;

  return {
    batchNo: batch.batchNumber || "",
    expiryDate: batch.expiryDate || "",
    vaccineCodeId:
      batch.vaccineCodeId === null || batch.vaccineCodeId === undefined
        ? null
        : String(batch.vaccineCodeId),
  };
};
